import { useEffect, useState } from "react";
import { Banner, Card, CardHead, Label, Pill, Skeleton, Toasts, useToasts } from "../components/ui";

// Desk control. Fires the market and operational conditions the desk has to
// cope with, so they can be shown happening rather than described.
//
// Route and API are both gated on simulation mode; in a build wired to real
// venues this page is unreachable and the endpoint answers 404.

interface ScenarioParam {
  key: string;
  label: string;
  type: string;
  default: string | number;
  options?: { value: string; label: string }[];
  suffix?: string;
}

interface Scenario {
  key: string;
  label: string;
  description: string;
  watchFor: string;
  group: string;
  params: ScenarioParam[];
}

interface SimState {
  scenarios: Scenario[];
  venues: { venue: string; label: string; firm: boolean }[];
  active: string[];
  controls: { orderFlowPerHour: number };
  recent: { scenario: string; note: string | null; actor: string; at: string }[];
}

const auth = () => ({
  authorization: `Bearer ${sessionStorage.getItem("otc.session") ?? ""}`,
  "content-type": "application/json",
});

const GROUP_LABEL: Record<string, string> = {
  liquidity: "Liquidity",
  market: "Market",
  flow: "Client flow",
  risk: "Risk & compliance",
  operations: "Operations",
};

export function DeskControl() {
  const [state, setState] = useState<SimState | null>(null);
  const [params, setParams] = useState<Record<string, Record<string, string>>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");
  const toast = useToasts();

  async function load() {
    const res = await fetch("/api/sim", { headers: auth() });
    if (!res.ok) {
      setError(res.status === 404 ? "Desk control is not enabled in this environment." : "Could not load desk control.");
      return;
    }
    const data: SimState = await res.json();
    setState(data);
    setParams((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const next: Record<string, Record<string, string>> = {};
      for (const s of data.scenarios) {
        next[s.key] = Object.fromEntries(s.params.map((p) => [p.key, String(p.default)]));
      }
      return next;
    });
  }

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, []);

  async function fire(s: Scenario) {
    setBusy(s.key);
    setError("");
    try {
      const res = await fetch("/api/sim/fire", {
        method: "POST",
        headers: auth(),
        body: JSON.stringify({ scenario: s.key, params: params[s.key] ?? {} }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.message ?? "failed");
      toast.push("ok", `${s.label}: ${body.message}`);
      await load();
    } catch (err) {
      toast.push("error", (err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  function setParam(scenario: string, key: string, value: string) {
    setParams((p) => ({ ...p, [scenario]: { ...(p[scenario] ?? {}), [key]: value } }));
  }

  if (error && !state) return <Banner tone="warn">{error}</Banner>;
  if (!state) return <Skeleton rows={6} />;

  const groups = [...new Set(state.scenarios.map((s) => s.group))];

  return (
    <>
      <p className="page-intro">
        Market and operational conditions, applied live against the simulated venues. Each is time-boxed and
        reverts itself.
      </p>

      <Card pad={false} className="enter">
        <CardHead title="Current conditions" />
        <div className="card-pad" style={{ display: "flex", justifyContent: "space-between", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {state.active.length === 0
              ? <Pill tone="good" dot>Steady state</Pill>
              : state.active.map((a) => <Pill key={a} tone="warn" dot>{a.replaceAll("_", " ")}</Pill>)}
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="t-label">Client flow</div>
            <div className="num" style={{ fontSize: 21, fontWeight: 600, letterSpacing: "-0.015em" }}>
              {state.controls.orderFlowPerHour}
              <span className="stat-unit">orders/hr</span>
            </div>
          </div>
        </div>
      </Card>

      {groups.map((group) => (
        <div className="section" key={group}>
          <Label>{GROUP_LABEL[group] ?? group}</Label>
          <div style={{ height: 12 }} />
          <div className="grid">
            {state.scenarios
              .filter((s) => s.group === group)
              .map((s) => (
                <Card key={s.key}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 300px", minWidth: 0 }}>
                      <div className="t-heading" style={{ marginBottom: 3 }}>{s.label}</div>
                      <div className="dim t-sm">{s.description}</div>
                      <div className="t-sm" style={{ color: "var(--accent)", marginTop: 6 }}>Watch: {s.watchFor}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                      {s.params.map((p) => (
                        <div className="field" key={p.key} style={{ margin: 0 }}>
                          <label htmlFor={`${s.key}-${p.key}`}>
                            {p.label}{p.suffix ? ` (${p.suffix})` : ""}
                          </label>
                          {p.options ? (
                            <select
                              id={`${s.key}-${p.key}`}
                              value={params[s.key]?.[p.key] ?? String(p.default)}
                              onChange={(e) => setParam(s.key, p.key, e.target.value)}
                              style={{ width: 140 }}
                            >
                              {p.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          ) : (
                            <input
                              id={`${s.key}-${p.key}`}
                              type="number"
                              step="any"
                              className="num"
                              value={params[s.key]?.[p.key] ?? String(p.default)}
                              onChange={(e) => setParam(s.key, p.key, e.target.value)}
                              style={{ width: 104 }}
                            />
                          )}
                        </div>
                      ))}
                      <button
                        className={`btn ${s.key === "reset" ? "btn-secondary" : "btn-primary"}`}
                        onClick={() => void fire(s)}
                        disabled={busy === s.key}
                        style={{ minWidth: 92 }}
                      >
                        {busy === s.key ? "Running" : s.key === "reset" ? "Reset" : "Run"}
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
          </div>
        </div>
      ))}

      {state.recent.length > 0 && (
        <div className="section">
          <Label>Recent activity</Label>
          <div style={{ height: 12 }} />
          <Card pad={false}>
            <div className="table-wrap flush">
              <table>
                <thead><tr><th>When</th><th>Scenario</th><th>Effect</th><th>By</th></tr></thead>
                <tbody>
                  {state.recent.map((e, i) => (
                    <tr key={`${e.at}-${e.scenario}-${i}`}>
                      <td className="dim num" style={{ whiteSpace: "nowrap" }}>
                        {new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                      </td>
                      <td style={{ fontWeight: 500 }}>{e.scenario.replaceAll("_", " ")}</td>
                      <td className="dim">{e.note ?? "—"}</td>
                      <td className="dim t-sm">{e.actor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      <Toasts items={toast.items} onDone={toast.dismiss} />
    </>
  );
}
